import { getApiBase } from "../runtimeConfig";

// =======================
// API base -valinta
// =======================
// Oletus tuotannossa → buukkeri
const PROD_DEFAULT = 'https://api.buukkeri.fi';

// Runtime-config (staging/prod sama buildi). Jos ei ole käytössä, palauttaa "".
const RUNTIME_BASE = (() => {
  try { return String(getApiBase?.() || "").trim(); }
  catch { return ""; }
})();

// Create React App -ympäristömuuttuja
const RAW_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) ||
  '';

const ENV_BASE = String(RAW_BASE).trim();

const RAW_USE_LOCAL =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_USE_LOCAL_API) ||
  '';
const USE_LOCAL = String(RAW_USE_LOCAL).toLowerCase() === 'true';

// --- Runtime-override (ei vaadi rebuildia) ---
const LS_OVERRIDE = (() => {
  try { return localStorage.getItem('bbx.apiBase') || ''; } catch { return ''; }
})();
// Salli kaikki alias-nimet overrideen: __BBX_API_BASE__, __API_BASE_OVERRIDE__, __API_BASE__
const WIN_OVERRIDE =
  (typeof window !== 'undefined'
    && (window.__BBX_API_BASE__ || window.__API_BASE_OVERRIDE__ || window.__API_BASE__)) || '';

export const BASE = (() => {
  // 1) Pakotettu arvo (LocalStorage tai window-muuttuja) voittaa kaiken
  const forced = String(LS_OVERRIDE || WIN_OVERRIDE).trim();
  if (forced) return forced.replace(/\/+$/, '');

  // 1.5) Runtime-config (ei vaadi rebuildia)
  if (RUNTIME_BASE) return RUNTIME_BASE.replace(/\/+$/, '');

  // 2) Build-aikainen env (CRA/Vite)
  if (ENV_BASE) return ENV_BASE.replace(/\/+$/, '');

  // 3) Devissä käytä local-API:a, jos flagi tai host on localhost
  const isLocalHost = typeof window !== 'undefined' &&
    ['localhost','127.0.0.1'].includes(window.location.hostname);
   if (USE_LOCAL || isLocalHost) {
    const proto = window.location.protocol || "http:";
    return `${proto}//${window.location.hostname}:8080`;
  }

  // 4) Fallback: sama origin (staging pysyy stagingissä)
  const origin =
    (typeof window !== "undefined" && window.location && window.location.origin)
      ? window.location.origin
      : "";
  if (origin) return origin.replace(/\/+$/, "");

  // 5) Viimeinen fallback → tuotanto
  return PROD_DEFAULT;
})();

export const API_BASE = `${BASE}/api`;
export const AUTH_BASE = `${API_BASE}/auth`;

// Staattinen media-base + fallback-kuva
const RAW_MEDIA_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_MEDIA_BASE) ||
  '';
export const MEDIA_BASE = (RAW_MEDIA_BASE ? String(RAW_MEDIA_BASE).trim() : `${BASE}/media`).replace(/\/+$/, '');
export const FALLBACK_BAND_IMAGE = `${MEDIA_BASE}/fallbacks/bandplaceholder.webp`;

// DEBUG: konsolilokit API-kutsuista (voit DevToolsissa: window.__API_DEBUG__ = true)
export const __API_DEBUG__ =
  typeof window !== "undefined"
    ? (window.__API_DEBUG__ ?? (["localhost","127.0.0.1"].includes(window.location.hostname)))
    : false;

// =======================
// PREVIEW/REVIEW TOKEN (vain esikatseluun)
// =======================
const PREVIEW_TOKEN_KEY = "bbx.previewToken";
let __previewToken = null;
// Lähetetäänkö preview-header/parametri tälle pyynnölle (rajataan polkuihin)
let __previewAllowed = false;

try {
  if (typeof window !== "undefined") {
const url = new URL(window.location.href);
const path = url.pathname || "";

 // hyväksy tarkasti previewToken TAI preview_token
 const hasPT = /(?:[?&])(previewToken|preview_token)=/.test(url.search);
 const allowPreviewOnThisPath =
   hasPT ||                                           // jos token on URLissa, hyväksy aina
   /^\/(?:preview|review)(?:\/|$)/i.test(path) ||     // /preview, /review
   /^\/backstage\/review(?:\/|$)/i.test(path) ||      // ✅ backstage-review tuotannossa
   /^\/esiintyja\/[^/]+/i.test(path) ||
   /^\/admin\/bands\/\d+/.test(path);

let t = null;
if (allowPreviewOnThisPath) {
  t = (
    url.searchParams.get("previewToken") ||
    url.searchParams.get("preview_token") ||
    ""
  ).trim();

  if (t) {
    __previewToken = t;
    sessionStorage.setItem(PREVIEW_TOKEN_KEY, t);
    ["previewToken", "preview_token"].forEach(k => url.searchParams.delete(k));
    window.history.replaceState({}, "", url.toString());
  } else {
    __previewToken = sessionStorage.getItem(PREVIEW_TOKEN_KEY) || null;
  }
} else {
  __previewToken = sessionStorage.getItem(PREVIEW_TOKEN_KEY) || null;
}
 // Päivitä sallittu-flag samassa yhteydessä
 __previewAllowed = !!allowPreviewOnThisPath;

  }
} catch {}
export const getPreviewToken = () => __previewToken;

// =======================
// TOKEN-HANDLERIT
// =======================
const TOKEN_STORAGE_KEY = "bbx_jwt";
let authToken = null;

// Samat avaimet kuin AuthContextissa – millisekunteina
const EXPIRY_KEYS = ["accessTokenExpiresAt","access_token_expires_at","token_expires_at"];
function __storeExpiryMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return;
  try {
    // peilaa useaan avaimeen, jotta mikä tahansa lukija löytää sen
    EXPIRY_KEYS.forEach(k => localStorage.setItem(k, String(n)));
  } catch {}
}
function __parseToMs(v) {
  if (v == null) return null;
  if (typeof v === "number") return v < 10_000_000_000 ? v * 1000 : v; // sec → ms
  const s = String(v).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return n < 10_000_000_000 ? n * 1000 : n; // sec → ms
  }
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
}
function __rememberExpiryFromBody(body) {
  try {
    const b = body || {};
    const candidates = [
      b.accessTokenExpiresAt, b.access_token_expires_at, b.token_expires_at,
      b.expiresAt, b.expires_at, b.exp, b.token_exp,
      // expires_in sekunteina (yleinen OAuth-kenttä) + token_expires_in
      (b.expires_in != null        ? (Date.now() + Number(b.expires_in)        * 1000) : null),
      (b.token_expires_in != null  ? (Date.now() + Number(b.token_expires_in)  * 1000) : null),
      b.data?.accessTokenExpiresAt, b.user?.accessTokenExpiresAt,
    ].filter(Boolean);
    for (const v of candidates) {
      const ms = __parseToMs(v);
      if (ms) { __storeExpiryMs(ms); return; }
    }
  } catch {}
}

try {
  authToken = localStorage.getItem(TOKEN_STORAGE_KEY) || null;
} catch {
  authToken = null;
}

export function setAuthToken(token) {
  authToken = token || null;
  try {
    if (authToken) localStorage.setItem(TOKEN_STORAGE_KEY, authToken);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);

    if (authToken) {
      try {
        const payload = JSON.parse(
          atob(authToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
        );
      if (payload?.exp) __storeExpiryMs(payload.exp * 1000);
      } catch {}
      } else {
      EXPIRY_KEYS.forEach((k) => localStorage.removeItem(k));
    }
  } catch {}
  
}

export function getAuthToken() {
  if (!authToken) {
    try {
      authToken = localStorage.getItem(TOKEN_STORAGE_KEY) || null;
    } catch {}
  }
  return authToken;
}

export function clearAuthToken() {
  setAuthToken(null);
}

// ----------------------- 401-listenerit -----------------------
const __unauthListeners = new Set();
export function onUnauthorized(fn) {
  if (typeof fn !== "function") return () => {};
  __unauthListeners.add(fn);
  return () => __unauthListeners.delete(fn);
}
function __emitUnauthorized(detail) {
  __unauthListeners.forEach(fn => { try { fn(detail); } catch {} });
}

// =======================
/* fetch-helperit */
// =======================
const isFormData = (v) => typeof FormData !== "undefined" && v instanceof FormData;

// --- Yhden kerran refresh-yritys rinnakkaistilanteiden varalle ---
let __refreshing = null;

/** Yritä kerran httpOnly-cookie refreshiä.
 *  Palauttaa true jos onnistui, false jos ei. Ei heitä virhettä ulos.
 */
async function __refreshOnce() {
  if (__refreshing) return __refreshing; // odota meneillään olevaa

  __refreshing = (async () => {
    try {
      const url = `${AUTH_BASE}/refresh`;
      // Käytetään raakaa fetchiä: EI Authorization-headeria, vain cookiet mukaan
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      // Poimi mahdollinen uusi access token ennen body-parsia
      const headerToken =
        res.headers.get("x-access-token") ||
        res.headers.get("x-auth-token") ||
        // joskus Authorization: Bearer xxx
        (() => {
          const h = res.headers.get("authorization");
          if (!h) return null;
          const m = /^Bearer\s+(.+)$/i.exec(h.trim());
          return m ? m[1] : null;
        })();

      // Hyödynnetään samaa parseria, jotta exp-aika tallentuu (header/body)
      const data = await handleJson(res); // heittää jos ei ok → siirtyy catchiin

      // Yritä löytää token myös bodystä
      const bodyToken =
        (data && typeof data === "object" && (
          data.access_token || data.token || data.jwt || data.bearer ||
          data.accessToken ||
          data.data?.access_token || data.data?.token || data.data?.jwt
        )) || null;

 const newToken = headerToken || bodyToken || null;
      if (newToken && typeof newToken === "string") {
        setAuthToken(newToken.trim());
      } else {
        clearAuthToken();
      }
      return res.ok === true;
    } catch {
      return false;
    } finally {
      __refreshing = null;
    }
  })();

  return __refreshing;
}


const handleJson = async (res, meta = {}) => {
  // ✅ 204/205: No Content → älä yritä JSON-parsintaa
  if (res.status === 204 || res.status === 205) {
    return { ok: true, status: res.status };
  }

  // Nosta exp header(e)ista jos tarjolla
  try {
    const h = (k) => res.headers?.get(k);
    const hExp =
      h("x-token-expires-at") ||
      h("x-access-token-expires-at") ||
      h("x-auth-exp") ||
      null;
    const hExpIn = h("x-token-expires-in"); // sekunteina
    if (hExp) {
      const ms = __parseToMs(hExp);
      if (ms) __storeExpiryMs(ms);
    } else if (hExpIn) {
      const ms = Date.now() + Number(hExpIn) * 1000;
      if (Number.isFinite(ms)) __storeExpiryMs(ms);
    }
  } catch {}

  // Lue text() kerran; voi olla tyhjä
  let raw = "";
  try { raw = await res.text(); } catch {}

  // Yritä parsia JSONiksi, muuten jätä stringiksi / nulliksi
  let body = null;
  if (raw) {
    try { body = JSON.parse(raw); }
    catch { body = raw; }
  }

  // Jos bodyssa tulee expi, muista se
  if (body && typeof body === "object") __rememberExpiryFromBody(body);

  // Tuunattu logitus: näytä JSON oikeasti luettavana
  if (!res.ok) {
    const pretty = typeof body === "string"
      ? body
      : (() => { try { return JSON.stringify(body, null, 2); } catch { return String(body); }})();
    console.error("[API ERROR]", res.status, pretty);
  }

  if (!res.ok) {
    const err = new Error(`${res.status} ${res.statusText}`);
    err.status = res.status;
    err.payload = body;
    err.url = meta?.url || res?.url || "";
    throw err;
  }
  return body ?? { ok: true, status: res.status };
};

const req = async (path, opts = {}) => {
  // Rakennetaan yksi suoritus (omitAuth=true poistaa Bearer-headerin)
  const doFetch = async (omitAuth = false) => {
    const hasBody = "body" in opts && opts.body != null;

    const headers = {
      Accept: "application/json",
      ...(hasBody && !isFormData(opts.body) ? { "Content-Type": "application/json" } : {}),
      ...(opts.headers || {}), // anna kutsujan headerit ensin…
      // …ja lisää omat vakioheaderit päälle
      ...(!omitAuth && getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
      ...(__previewToken && __previewAllowed ? { "X-Preview-Token": __previewToken } : {}),
    };
   // Jos omitAuth → poista Authorization riippumatta siitä, mistä se tuli (myös pienaakkosversio)
    if (omitAuth) {
      delete headers.Authorization; delete headers.authorization;
    }

    // Absoluuttinen URL vai API_BASE-prefiksattu
    let url = path.startsWith("http")
      ? path
      : `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;

    // Lisää previewToken GET-kyselyihin myös querynä (jos ei jo ole)
    if (__previewToken && __previewAllowed && (!opts.method || String(opts.method).toUpperCase() === "GET")) {
      try {
        const u = new URL(url);
        if (!u.searchParams.has("previewToken") && !u.searchParams.has("preview_token")) {
          u.searchParams.set("previewToken", __previewToken);
          url = u.toString();
        }
      } catch { /* no-op */ }
    }

    // dynaaminen debug-kytkin
    const DBG = (typeof window !== "undefined" && typeof window.__API_DEBUG__ !== "undefined")
      ? !!window.__API_DEBUG__
      : !!__API_DEBUG__;

    if (DBG) {
      const safeHeaders = { ...headers, ...(headers.Authorization ? { Authorization: "Bearer ***" } : {}), ...(headers.authorization ? { authorization: "Bearer ***" } : {}) };
      const bodyPreview = typeof opts.body === "string" && opts.body.length > 200
        ? opts.body.slice(0, 200) + "…"
        : opts.body;
      console.debug("[API →]", (opts.method || "GET"), url, { headers: safeHeaders, body: bodyPreview });
    }

    const res = await fetch(url, {
      ...opts,
      credentials: "include",
      headers,
    });

    const out = await handleJson(res, { url }); // heittää jos !res.ok

    if (DBG) console.debug("[API ←]", res.status, url, out);

    // 🔔 Kalenterin muokkaus -ping (ei-GET)
    try {
      const method = String(opts?.method || "GET").toUpperCase();
      if (method !== "GET") {
        const target = typeof path === "string" ? path : url;
        const isCalendarish = /(\/calendar(\/|$)|\/availability(\/|$)|\/bookings\/(availability|calendar|owner|open|close))/i.test(target);
        if (isCalendarish) notifyCalendarTouched();
      }
    } catch {}

    return out;
  };

  // 1) Ensimmäinen yritys normaalisti
  try {
    return await doFetch(false);
  } catch (e) {
    let finalError = e;

    const isAuthError =
      e?.status === 401 ||
      e?.status === 419 ||
      ["token_expired", "jwt_expired"].includes(
        String(e?.payload?.reason || e?.payload?.error || "").toLowerCase()
      );

    // 2) Access-token vanheni → yritä refresh KERRAN ja toista pyyntö uudella tokenilla.
    if (isAuthError && !opts.__retried) {
      const refreshed = await __refreshOnce();

      if (refreshed) {
        try {
          // __refreshOnce tallentaa uuden access-tokenin,
          // joten retry käyttää normaalia Authorization-headeria.
          return await doFetch(false);
        } catch (e2) {
          finalError = e2;
        }
      }
    }

    // 3) Ilmoita session päättymisestä vasta kun refresh/retry ei pelastanut pyyntöä.
    const finalReason = String(
      finalError?.payload?.reason || finalError?.payload?.error || ""
    ).toLowerCase();

    if (
      finalError?.status === 401 ||
      finalError?.status === 419 ||
      finalReason === "token_expired" ||
      finalReason === "jwt_expired"
    ) {
      __emitUnauthorized({
        status: finalError?.status,
        body: finalError?.payload,
        url: finalError?.url || "",
      });
    }

    throw finalError;
  }
};


function notifyCalendarTouched() {
  try {
    localStorage.setItem('bilebox:calendar-touched', String(Date.now()));
    window.dispatchEvent(new Event('bb:calendar:touched')); // sama välilehti
  } catch {}
}

// --- Kevyt client-cache / in-flight dedupe bändilistauksille ---
// Tarkoitus: jos etusivu, LatestArtists ja MonthlyFeatured pyytävät saman
// /bands?limit=50&published=1 -listan samaan aikaan, tehdään vain yksi fetch.
const BANDS_LIST_CACHE_TTL_MS = 10_000;
const __bandsListCache = new Map(); // path -> { expiresAt, data }
const __bandsListInflight = new Map(); // path -> Promise

let __genresCache = null;
let __genresPromise = null;

let __regionsCache = null;
let __regionsPromise = null;

// --- Vapaat tekijät -haun kevyt in-flight dedupe ---
// Estää dev/React StrictMode -tuplat, kun sama /bands/free?date=... lähtee kahdesti.
const __freeBandsInflight = new Map(); // path -> Promise

function makeAbortError() {
  const err = new Error("Request aborted");
  err.name = "AbortError";
  return err;
}

function withAbortSignal(promise, signal) {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(makeAbortError());

  return new Promise((resolve, reject) => {
    const onAbort = () => reject(makeAbortError());

    signal.addEventListener("abort", onAbort, { once: true });

    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      }
    );
  });
}

 async function reqFirstOk(paths, opts, retryOn = new Set([404, 405, 503])) {
   let lastErr = null;
   for (const p of paths) {
     try {
       return await req(p, opts);
     } catch (e) {
       lastErr = e;
       // Fallbackkaa vain jos reitti/metodi puuttuu tai palvelin ei ole saavutettavissa.
       if (retryOn.has(e?.status)) continue;
       throw e; // 400/401/409/422 jne. = oikea virhe, ei yritetä toista reittiä
     }
   }
   throw lastErr || new Error("All API paths failed");
 }

// Pieni apuri: JSON-body helposti
const json = (obj) => JSON.stringify(obj ?? {});

// ---------- Media-normalisointi UI:lle ----------
const pick = (...xs) => xs.find((v) => v != null && v !== "");
const norm = {
  image: (x) => {
    const r = x || {};
    return {
      id: r.id ?? r.image_id ?? r.uuid ?? null,
      url: pick(r.url, r.image_url, r.location, r.path, r.file) || null,
      position: r.position ?? null,
    };
  },
  video: (x) => {
    const r = x || {};
    return {
      id: r.id ?? null,
      url: pick(r.url, r.video_url, r.embed_url) || null,
      position: r.position ?? null,
    };
  },
  audio: (x) => {
    const r = x || {};
    return {
      id: r.id ?? null,
      url: pick(r.url, r.track_url, r.audio_url) || null,
      position: r.position ?? null,
    };
  },
};

// ---- Compat: yhtenäistä hinnoittelun avaimet julkisesta/admin-vastauksesta ----
function normalizeBandPricingCompat(raw) {
  const band = raw?.band ?? raw;
  if (!band || typeof band !== "object") return raw;

  try {
    // Jos pricing on jo mukana → yhdenmukaista avaimet
    if (Array.isArray(band.pricing)) {
      band.pricing = band.pricing.map((p) => {
        const price = p?.price ?? p?.base_price ?? null;
        const set_label = (p?.set_label ?? p?.label ?? "").trim();
        const region_id = Number(p?.region_id ?? p?.regionId ?? p?.region) || null;
        return { ...p, region_id, set_label, price };
      });
    }
    // Muussa tapauksessa käytä band_regions_pricing -listaa varakenttänä
    else if (Array.isArray(band.band_regions_pricing)) {
      band.pricing = band.band_regions_pricing.map((p) => {
        const price = p?.price ?? p?.base_price ?? null;
        const set_label = (p?.set_label ?? p?.label ?? "").trim();
        const region_id = Number(p?.region_id ?? p?.regionId ?? p?.region) || null;
        return { region_id, set_label, price };
      });
    }
  } catch (e) {
    try { console.debug("[api] normalizeBandPricingCompat failed:", e); } catch {}
  }
  return band;
}

// ---- URL-helper välimuistin ohitukseen ----
const _appendTs = (path, ts) =>
  ts ? (path.includes("?") ? `${path}&ts=${ts}` : `${path}?ts=${ts}`) : path;



// =======================
// Julkinen API
// =======================
export const api = {

  // Yleinen GET-wrapper, axios-tyyliin: api.get(path, { params, ...opts })
  get(path, opts = {}) {
    const { params, ...rest } = opts || {};
    let finalPath = path;

    if (params && typeof params === "object") {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        if (Array.isArray(value)) {
          value.forEach((v) => qs.append(key, String(v)));
        } else {
          qs.append(key, String(value));
        }
      });
      const query = qs.toString();
      if (query) {
        finalPath += (finalPath.includes("?") ? "&" : "?") + query;
      }
    }

    return req(finalPath, rest);
  },

  // --- Analytics ---
  analytics: {
    performer: (
      bandId,
      {
        setLabel,
        customerType,
        regionId,
        seasonKey = "default",
      } = {}
    ) => {
      const params = new URLSearchParams();

      if (setLabel) {
        params.set("setLabel", String(setLabel));
      }

      if (customerType) {
        params.set("customerType", String(customerType));
      }

      if (regionId != null) {
        params.set("regionId", String(regionId));
      }

      if (seasonKey) {
        params.set("seasonKey", String(seasonKey));
      }

      const query = params.toString();

      return req(
        `/analytics/performer/${encodeURIComponent(String(bandId))}${
          query ? `?${query}` : ""
        }`
      );
    },
  },

// --- Auth ---
auth: {
  changePassword: ({ current_password, new_password }) =>
    req("/auth/password", {
      method: "PUT",
      body: json({ current_password, new_password }),
    }),

  /** Kirjautuminen
   * @param {string} email
   * @param {string} password
   * @param {{remember?: boolean}} [opts] - Kun true, välitetään backendiin remember-lippu (pitkäkestoinen eväste).
   */
  login: (email, password, opts = {}) =>
    req("/auth/login", {
      method: "POST",
      // HUOM: req()n kannattaa välittää credentials: 'include' httpOnly-evästeitä varten.
      // Jos se EI tee niin, lisää se req-wrapperiin.
      body: json({
        email,
        password,
        ...(typeof opts.remember === "boolean" ? { remember: !!opts.remember } : {}),
      }),
    }),

  register: (payload) =>
    req("/auth/register", { method: "POST", body: json(payload) }),

  logout: () => req("/auth/logout", { method: "POST" }),

  me: () => req("/auth/me"),

  meBands: () => req("/auth/me/bands"),

  googleUrl: () => `${AUTH_BASE}/google`,

  accept: (payload) =>
    req("/auth/accept", { method: "POST", body: json(payload) }),

  passwordForgot: (email) =>
    req("/auth/password/forgot", { method: "POST", body: json({ email }) }),

  passwordReset: (token, new_password) =>
    req("/auth/password/reset", {
      method: "POST",
      body: json({ token, new_password }),
    }),

  sendVerifyEmail: (payload = {}) =>
    req("/auth/verify-email/send", {
      method: "POST",
      body: json(payload),
    }).then(() => true),

  /* ✉️ Sähköpostivahvistus: vahvista token
     Heittää virheen jos token on virheellinen / vanhentunut. */
  confirmVerifyEmail: (token) =>
    req("/auth/verify-email/confirm", {
      method: "POST",
      body: json({ token }),
    }).then(() => true),

  /** 🗑️ Tilin poisto (204 No Content tuettu)
   *  Kokeile ensisijaisesti /auth/me, fallback /account/me?confirm=1
   *  Heittää virheen jos esim. 409 owns_bands (UI näyttää toastin).
   */
  async deleteAccount() {
    await reqFirstOk(
      ["/auth/me", "/account/me?confirm=1"],
      { method: "DELETE" }
    );
    return true;
  },

  // UUSI: käyttäjän perustietojen päivitys – proovaa useita reittejä.
  async updateMe(payload = {}) {
    // 1) Suorat me-reitit – aloita POSTilla (kiertää edge/WAF PUT/PATCH -blokit)
    const meTries = [
      { path: "/auth/me",  method: "POST"  },
      { path: "/users/me", method: "POST"  },
      { path: "/auth/me",  method: "PUT"   },
      { path: "/users/me", method: "PUT"   },
      { path: "/auth/me",  method: "PATCH" },
      { path: "/users/me", method: "PATCH" },
    ];

    let lastErr;
    for (const t of meTries) {
      try {
        return await req(t.path, { method: t.method, body: json(payload) });
      } catch (e) {
        lastErr = e;
        if (e?.status === 404 || e?.status === 405 || e?.status === 503) {
          continue;
        }
        throw e;
      }
    }

    // 2) Fallback: hae oma id ja käytä admin-reittiä, joka sallii myös JWT:n (adminTokenOrJwt)
    try {
      const me = await req("/auth/me"); // { ok, user:{ id, ... } }
      const myId = me?.user?.id ?? me?.id;
      if (!Number.isFinite(Number(myId))) {
        throw lastErr || new Error("Cannot resolve user id for updateMe");
      }
      try {
        return await req(`/admin/users/${encodeURIComponent(myId)}`, {
          method: "PUT",
          body: json(payload),
        });
      } catch (e2) {
        if (e2?.status === 405 || e2?.status === 404) {
          return await req(`/admin/users/${encodeURIComponent(myId)}`, {
            method: "PATCH",
            body: json(payload),
          });
        }
        throw e2;
      }
    } catch (e) {
      if (e?.status === 404 || e?.status === 405) {
        throw lastErr || e;
      }
      throw e;
    }
  },
},

  // --- Invoices (laskut) ---
  invoices: {
    /**
     * Luo kertakäyttöisen lataustokenin ja palauttaa suoran PDF-URL:n.
     * Palauttaa backendin rungon sellaisenaan (esim. { ok, url, expires_at, one_time }).
     */
    async issueToken(id) {
      const iid = encodeURIComponent(id);
      return await reqFirstOk(
        [
          `/invoices/${iid}/issue-token`,
          `/admin/invoices/${iid}/issue-token`, // fallback jos admin-alias käytössä
        ],
        { method: "POST", body: JSON.stringify({}) }
      );
    },

    /**
     * Sokeri: pyydä token ja avaa latausikkuna.
     * Palauttaa { ok:true, url } jos URL saatiin.
     */
    async downloadOnce(id) {
      const r = await this.issueToken(id);
      const url = r?.url || r?.download_url || r?.href;
      if (url) {
        try { window.open(url, "_blank", "noopener,noreferrer"); } catch {}
        return { ok: true, url };
      }
      return r;
    },
  },



  // --- Meta ---
  health: (opts) => req("/healthz", opts),
  artistTypes: (opts) => req("/meta/artist_types", opts),

  // Palauttaa aina taulukon muodossa: [{ id: string|number, name: string }, ...]
  async genres(opts = {}) {
    // Genret muuttuvat harvoin. Cache + in-flight dedupe estää dev/StrictMode-tuplat
    // ja sen, että abortattu ensimmäinen kutsu jatkaisi turhiin fallbackeihin.
    if (__genresCache) {
      return __genresCache;
    }

    if (__genresPromise) {
      return withAbortSignal(__genresPromise, opts?.signal);
    }

    const normalizeGenres = (raw) => {
      const rows =
        Array.isArray(raw?.items) ? raw.items :
        Array.isArray(raw?.genres) ? raw.genres :
        Array.isArray(raw?.data?.items) ? raw.data.items :
        Array.isArray(raw?.data) ? raw.data :
        Array.isArray(raw) ? raw :
        [];

      return rows
        .map((g, i) => {
          if (typeof g === "string") {
            return {
              id: `legacy:${i + 1}`,
              name: String(g).trim(),
            };
          }

          return {
            id: g?.id ?? g?.value ?? `genre:${i + 1}`,
            name: String(g?.name ?? g?.label ?? g?.title ?? "").trim(),
          };
        })
        .filter((g) => g.name);
    };

    const fetchGenres = async () => {
      // 1) Uusi PG-reitti: /bands/genres → { items:[{id,name}] }
      try {
        const r = await req("/bands/genres", { ...(opts || {}), signal: undefined });
        const normalized = normalizeGenres(r);
        if (normalized.length) return normalized;
      } catch (e) {
        if (e?.name === "AbortError") throw e;
      }

      // 2) Vanha: /genres
      try {
        const r = await req("/genres", { ...(opts || {}), signal: undefined });
        const normalized = normalizeGenres(r);
        if (normalized.length) return normalized;
      } catch (e) {
        if (e?.name === "AbortError") throw e;
      }

      // 3) Legacy: /meta/genres
      try {
        const legacy = await req("/meta/genres", { ...(opts || {}), signal: undefined });
        return normalizeGenres(legacy);
      } catch (e) {
        if (e?.name === "AbortError") throw e;
      }

      return [];
    };

    __genresPromise = fetchGenres()
      .then((items) => {
        __genresCache = items;
        return items;
      })
      .finally(() => {
        __genresPromise = null;
      });

    return withAbortSignal(__genresPromise, opts?.signal);
  },

  // Palauttaa string-listan maakunnista (yleiskäyttö).
  regions: async (opts = {}) => {
    // Maakunnat muuttuvat harvoin. Cache + in-flight dedupe estää dev/StrictMode-tuplat.
    if (__regionsCache) {
      return __regionsCache;
    }

    if (__regionsPromise) {
      return withAbortSignal(__regionsPromise, opts?.signal);
    }

    const fetchRegions = async () => {
      // Jaettu haku EI käytä yksittäisen komponentin AbortControlleria,
      // jotta StrictMode-abortti ei katkaise samaa hakua muilta.
      const safeOpts = { ...(opts || {}), signal: undefined };

      const res = await reqFirstOk(
        ["/public/pricing/regions", "/bands/regions", "/meta/regions"],
        safeOpts
      ).catch(() => null);

      const rows = Array.isArray(res?.items)
        ? res.items
        : (Array.isArray(res) ? res : []);

      if (!rows.length) return [];

      return rows
        .map((r) => (typeof r === "string" ? { name: r } : r))
        .map((r) => String(r?.name ?? r?.region ?? r?.label ?? "").trim())
        .filter(Boolean);
    };

    __regionsPromise = fetchRegions()
      .then((items) => {
        __regionsCache = items;
        return items;
      })
      .finally(() => {
        __regionsPromise = null;
      });

    return withAbortSignal(__regionsPromise, opts?.signal);
  },

  /* ------------ Esiintyjät ------------ */
    bands: (params = {}, opts) => {
    // ✅ Tukee sekä api.bands({ signal }) että
    // api.bands({ region: "Uusimaa", signal }).
    // Request-optiot erotetaan hakuehdoista niin, ettei region/date/query katoa.
    if (
      opts === undefined &&
      params &&
      typeof params === "object"
    ) {
      const {
        signal,
        headers,
        method,
        ...bandParams
      } = params;

      const hasInlineRequestOptions =
        signal !== undefined ||
        headers !== undefined ||
        method !== undefined;

      if (hasInlineRequestOptions) {
        opts = {
          ...(signal !== undefined ? { signal } : {}),
          ...(headers !== undefined ? { headers } : {}),
          ...(method !== undefined ? { method } : {}),
        };

        params = bandParams;
      }
    }

    const q = new URLSearchParams();
    if (params.query) q.set("query", params.query);
    if (params.genre) q.set("genre", params.genre);
    if (params.region) q.set("region", String(params.region));
    if (params.page) q.set("page", String(params.page));
    if (params.limit) q.set("limit", String(params.limit));
    if (params.published != null) q.set("published", String(params.published));
    if (params.date) q.set("date", params.date);
    const path = q.toString() ? `/bands?${q.toString()}` : `/bands`;

    // Cachetetaan vain tavalliset GET-listaukset, ei esim. yksittäisiä admin-/muokkausreittejä.
    const method = String(opts?.method || "GET").toUpperCase();
    const isCacheableList = method === "GET" && path.startsWith("/bands?");

    if (!isCacheableList) {
      return req(path, opts);
    }

    const now = Date.now();
    const cached = __bandsListCache.get(path);
    if (cached && cached.expiresAt > now) {
      return withAbortSignal(Promise.resolve(cached.data), opts?.signal);
    }

    const inflight = __bandsListInflight.get(path);
    if (inflight) {
      return withAbortSignal(inflight, opts?.signal);
    }

    // Huom: varsinainen yhteinen fetch ei käytä yksittäisen komponentin AbortControlleria,
    // jotta yhden komponentin abort ei katkaise samaa jaettua hakua muilta.
    // Kutsujalle palautuva wrapper kuitenkin kunnioittaa sen omaa signal-arvoa.
    const sharedPromise = req(path, { ...(opts || {}), signal: undefined })
      .then((data) => {
        __bandsListCache.set(path, {
          expiresAt: Date.now() + BANDS_LIST_CACHE_TTL_MS,
          data,
        });
        return data;
      })
      .finally(() => {
        __bandsListInflight.delete(path);
      });

    __bandsListInflight.set(path, sharedPromise);
    return withAbortSignal(sharedPromise, opts?.signal);
  },

createBand: async (payload = {}) => {
  // 1) yritä ensin { band: ... } – tämä on palvelimesi ensisijainen muoto
  try {
    return await req(`/bands`, {
      method: "POST",
      body: JSON.stringify({ band: payload }),
    });
  } catch (e1) {
    // 2) fallback: paljas payload
    try {
      return await req(`/bands`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
    } catch (e2) {
      // 3) viimeinen yritys: tee uniikki slug ja kokeile molemmat muodot
      const ts = Date.now().toString(36).slice(-4);
      const retry = { ...payload, slug: `${String(payload.slug || "band")}-${ts}` };

      try {
        return await req(`/bands`, {
          method: "POST",
          body: JSON.stringify({ band: retry }),
        });
      } catch {
        return await req(`/bands`, {
          method: "POST",
          body: JSON.stringify(retry),
        });
      }
    }
  }
},

  /* ------------ Genres (save for a band) ------------ */
  getBandGenres(bandId, opts) {
    return req(`/bands/${encodeURIComponent(bandId)}/genres`, opts);
  },

  // Säilytä taaksepäin yhteensopiva alias: api.deleteAccount -> api.auth.deleteAccount
  async deleteAccount() {
    // Delegoi yhtenäiseen apuriin: sisältää 204-tuetun parserin ja fallback-reitit
    await api.auth.deleteAccount();
    return true;
  },

  async saveBandGenres(bandId, genreIds = [], opts = {}) {
    const ids = Array.from(new Set(
      (genreIds || [])
        .map(v => {
          const s = String(v).trim();
          if (/^\d+$/.test(s)) return Number(s);
          const m = s.match(/\b(\d+)\b/);
          return m ? Number(m[1]) : null;
        })
        .filter(n => Number.isFinite(n))
    )).slice(0, 6);

    return req(
      `/bands/${encodeURIComponent(bandId)}/genres`,
      { method: "PUT", body: JSON.stringify({ genre_ids: ids }), ...(opts || {}) }
    );
  },

  // Haku ID:llä TAI slugilla (public-first, sitten admin)
  async bandByIdOrSlug(idOrSlug, { signal, noCache = true } = {}) {
    const raw = String(idOrSlug).trim();
    const isId = /^\d+$/.test(raw);
    const slug = encodeURIComponent(raw);
    const hasToken = !!getAuthToken();

    const ts = noCache ? Date.now() : null;
    const paths = isId
      // ID: kokeile julkinen ensin → admin jos token
      ? [
          _appendTs(`/bands/${raw}`, ts),
          ...(hasToken ? [_appendTs(`/admin/bands/${raw}`, ts)] : []),
        ]
      // SLUG: julkinen profiili → query-varapolku → admin-by-slug jos token
      : [
          _appendTs(`/bands/${slug}`, ts),
          _appendTs(`/bands?slug=${slug}`, ts),
          ...(hasToken ? [_appendTs(`/admin/bands/by-slug/${slug}`, ts)] : []),
        ];

    const data = await reqFirstOk(paths, { signal });
      return normalizeBandPricingCompat(
      data?.band ?? data?.item ?? data?.data?.band ?? data
    );
  },

  bandById: (id, opts) => api.bandByIdOrSlug(id, opts),


  // --- Päivitys bändille id:llä ---
  updateBand: async (id, payload = {}) => {
    // Jos payloadissa ei ole tiedostoja → JSON suoraan
    const hasFile =
      Object.values(payload).some(
        v => (typeof File !== "undefined" && v instanceof File) ||
             (typeof Blob !== "undefined" && v instanceof Blob)
      );

    // Jos ei tiedostoja, lähetä JSON — arrayt säilyvät oikeina
    if (!hasFile) {
      return reqFirstOk(
        [
          `/bands/${encodeURIComponent(id)}`,
          `/admin/bands/${encodeURIComponent(id)}`,
        ],
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
    }

    // Jos mukana tiedostoja → lähetä FormData turvallisesti
    const fd = new FormData();
    for (const [key, val] of Object.entries(payload)) {
      if (val == null) continue;
      if (Array.isArray(val)) {
        // tärkein korjaus: FormData muuttaa [2,5] → "2,5"
        // joten paketoidaan se JSONiksi
        fd.append(key, JSON.stringify(val));
      } else {
        fd.append(key, val);
      }
    }

    return reqFirstOk(
      [
        `/bands/${encodeURIComponent(id)}`,
        `/admin/bands/${encodeURIComponent(id)}`,
      ],
      { method: "PUT", body: fd }
    );
  },

  // --- Some-linkit bändille ---
  // Top-level, jotta kutsu on api.updateBandSocials(id, payload)
  // Yritä julkinen reitti ensin, fallback adminiin jos sellainen on.
  updateBandSocials: (id, body) =>
    reqFirstOk(
      [
        `/bands/${encodeURIComponent(id)}/socials`,
        `/admin/bands/${encodeURIComponent(id)}/socials`,
      ],
      { method: "PUT", body: json(body) }
    ),


  // --- Päivitys bändille slugilla ---
  updateBandBySlug: (slug, payload) =>
    req(`/bands/by-slug/${encodeURIComponent(slug)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  // --- Publish/unpublish ---
  publishBand: (id, publish = true) =>
    reqFirstOk(
      [
        `/bands/${encodeURIComponent(id)}/publish`,
        `/admin/bands/${encodeURIComponent(id)}/publish`,
      ],
      { method: "POST", body: JSON.stringify({ publish }) }
    ),

     // --- Julkaisun tarkistuslista (review-check) ---
  reviewCheck: (bandId) =>
    reqFirstOk(
      [
        `/bands/${encodeURIComponent(bandId)}/review-check`,
        `/admin/bands/${encodeURIComponent(bandId)}/review-check`,
      ],
      { method: "GET" }
    ),

      // --- Lähetä profiili tarkistettavaksi ---
    submitForReview: async (bandId, { force = true } = {}) => {
    // 0) Varmista että bändillä on profiilikuva
    try {
      const b = await api.bandByIdOrSlug(bandId).catch(() => ({}));
      const img = (b?.profile_image || b?.profile_image_url || "").trim();
      if (!img) {
        const err = new Error("profile_image_required");
        err.status = 400;
        err.payload = { error: "profile_image_required", message: "Lisää profiilikuva ennen julkaisua." };
        throw err;
      }
    } catch (e) {
      if (e?.payload?.error === "profile_image_required") throw e;
      // jos band-haku epäonnistuu, jatketaan varovasti — backend voi vielä estää
    }
    const id = encodeURIComponent(bandId);
    const qs = force ? "?force=1" : "";
    return reqFirstOk(
      [
        `/bands/${id}/submit-for-review${qs}`,
        `/admin/bands/${id}/submit-for-review${qs}`,
      ],
      { method: "POST", body: force ? JSON.stringify({ force: true }) : undefined }
    );
  },

  freeBands: (date, opts = {}) => {
    // opts voi sisältää sekä fetch-optioita (signal) että query-parametreja (region)
    const { region, params, ...fetchOpts } = (opts && typeof opts === "object") ? opts : {};
    const regionValue = region ?? params?.region ?? null;

    const q = new URLSearchParams();
    if (date) q.set("date", String(date));
    if (regionValue) q.set("region", String(regionValue));

    const qs = q.toString() ? `?${q.toString()}` : "";
    const path = `/bands/free${qs}`;

    // Jaetaan sama käynnissä oleva free-haku usealle kutsujalle.
    // Varsinainen fetch ei käytä yksittäisen komponentin AbortControlleria,
    // jotta StrictMode/unmount ei peru samaa hakua toiselta kutsujalta.
    const method = String(fetchOpts?.method || "GET").toUpperCase();
    if (method !== "GET") {
      return req(path, fetchOpts);
    }

    const inflight = __freeBandsInflight.get(path);
    if (inflight) {
      return withAbortSignal(inflight, fetchOpts?.signal);
    }

    const sharedPromise = req(path, { ...fetchOpts, signal: undefined })
      .finally(() => {
        __freeBandsInflight.delete(path);
      });

    __freeBandsInflight.set(path, sharedPromise);
    return withAbortSignal(sharedPromise, fetchOpts?.signal);
  },

  // Hakee bändin id:llä TAI slugilla (palauttaa suoraan band-olion)
band: (idOrSlug, opts) => api.bandByIdOrSlug(idOrSlug, opts),


  /* ------------ Profiilin keikat ------------ */
  profileGigs: {
    list(bandId, opts = {}) {
      return req(
        `/admin/bands/${encodeURIComponent(bandId)}/profile-gigs`,
        opts
      );
    },

    create(bandId, payload = {}, opts = {}) {
      return req(
        `/admin/bands/${encodeURIComponent(bandId)}/profile-gigs`,
        {
          ...(opts || {}),
          method: "POST",
          body: json(payload),
        }
      );
    },

    update(bandId, gigId, payload = {}, opts = {}) {
      return req(
        `/admin/bands/${encodeURIComponent(bandId)}/profile-gigs/${encodeURIComponent(gigId)}`,
        {
          ...(opts || {}),
          method: "PATCH",
          body: json(payload),
        }
      );
    },

    remove(bandId, gigId, opts = {}) {
      return req(
        `/admin/bands/${encodeURIComponent(bandId)}/profile-gigs/${encodeURIComponent(gigId)}`,
        {
          ...(opts || {}),
          method: "DELETE",
        }
      );
    },
  },


  /* ------------ Kalenteri ------------ */
  calendar: async (bandId, { from, to, signal } = {}) => {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    q.set("v", String(Date.now())); // estä välimuisti
    const qs = q.toString() ? `?${q.toString()}` : "";

    // uusi → fallback vanhaan
    const data = await reqFirstOk(
      [
        `/bookings/calendar/${encodeURIComponent(bandId)}${qs}`,
        `/calendar/${encodeURIComponent(bandId)}${qs}`,
      ],
      { signal }
    );

    const events = (data.events || []).map((e) => ({
      ...e,
      status: e.status || e.extendedProps?.status || "open",
    }));
    return { ok: data.ok ?? data.success ?? true, events };
  },

availability: {
  async list(bandId, { from, to, signal } = {}) {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    const qs = q.toString() ? `?${q.toString()}` : "";

    const res = await reqFirstOk(
      [
        `/bookings/availability/${encodeURIComponent(bandId)}${qs}`,
        `/bands/${encodeURIComponent(bandId)}/availability${qs}`,
        `/availability/${encodeURIComponent(bandId)}${qs}`,
      ],
      { signal }
    );

    const rawAvail = res?.availability ?? res ?? {};
    const meta = res?.meta || {}; // ⬅️ UUSI

    const out = {};
    if (Array.isArray(rawAvail)) {
      rawAvail.forEach(d => { out[String(d).slice(0, 10)] = true; });
    } else if (rawAvail && typeof rawAvail === "object") {
      Object.entries(rawAvail).forEach(([d, v]) => {
        const open = v === true || v === 1 || v === "1" || String(v).toLowerCase() === "open";
        if (open) out[String(d).slice(0, 10)] = true;
      });
    }
    return { availability: out, meta }; // ⬅️ PALAUTA META MUKANA
  },

  set(bandId, date, isOpen, { signal } = {}) {
    return req(`/bookings/availability/${encodeURIComponent(bandId)}`, {
      method: "POST",
      body: JSON.stringify({ date, state: isOpen ? "open" : "closed" }),
      signal,
    });
  },
  open(bandId, date, opts) { return this.set(bandId, date, true, opts); },
  close(bandId, date, opts) { return this.set(bandId, date, false, opts); },
},



  /* ------------ Yritykset ------------ */
  companies: (params = {}, opts) => {
    const q = new URLSearchParams();
    if (params.query) q.set("query", params.query);
    if (params.region) q.set("region", params.region);
    if (params.page) q.set("page", String(params.page));
    if (params.limit) q.set("limit", String(params.limit));
    const path = q.toString() ? `/companies?${q.toString()}` : `/companies`;
    return req(path, opts);
  },

  freeCompanies: async (date, opts) => {
    const qs = date ? `?date=${encodeURIComponent(date)}` : "";
    try {
      return await req(`/companies/free${qs}`, opts);
    } catch {
      return req(`/companies`, opts);
    }
  },

  company: (slug, opts) => req(`/companies/${encodeURIComponent(slug)}`, opts),

  companyCalendar: (companyId, { from, to, signal } = {}) => {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    const qs = q.toString() ? `?${q.toString()}` : "";
    return req(`/bookings/company-calendar/${encodeURIComponent(companyId)}${qs}`, { signal });
  },

  /* ------------ Palaute / analytiikka ------------ */
  feedback: Object.assign(
    // Taaksepäinyhteensopiva: sallii yhä varmentamattoman palautteen jos backissa on päällä
    function feedback(payload = {}, opts) {
      return req("/feedback", { method: "POST", body: json(payload), ...(opts || {}) });
    },
    {
      /** PIN-tarkistus ennen lomakkeen avaamista */
      validate({ band_id, company_id, user_email, event_date, feedback_pin }, opts) {
        return req("/feedback/validate", {
          method: "POST",
          body: json({ band_id, company_id, user_email, event_date, feedback_pin }),
          ...(opts || {}),
        });
      },

      /** Vahvistettu palautteen lähetys (PIN tai linkin mukana tullut PIN) */
      submit(
        { band_id, company_id, user_name, user_email, rating, comment, event_date, feedback_pin },
        opts
      ) {
        return req("/feedback/submit", {
          method: "POST",
          body: json({ band_id, company_id, user_name, user_email, rating, comment, event_date, feedback_pin }),
          ...(opts || {}),
        });
      },

      /** Dev: PINin johdanta debugiin */
      derivePinDev({ band_id, company_id, email, date }, opts) {
        const q = new URLSearchParams();
        if (band_id) q.set("band_id", band_id);
        if (company_id) q.set("company_id", company_id);
        if (email) q.set("email", email);
        if (date) q.set("date", date);
        return req(`/feedback/_dev/derive-pin?${q.toString()}`, { method: "GET", ...(opts || {}) });
      },

      /** Yhtenäinen virhetulkki UI:lle */
      interpretError(err) {
        const status = err?.status;
        const reason = err?.payload?.reason;

        if (reason === "not_active_yet" && err?.payload?.message) {
          return { code: reason, message: err.payload.message };
        }

        const map = {
          not_found: "Varausta ei löytynyt annetulla sähköpostilla ja päivällä.",
          not_confirmed: "Varausta ei ole vielä vahvistettu.",
          not_active_yet: "Palaute ei ole vielä avautunut.",
          expired: "Palautteen aikaikkuna on päättynyt.",
          already_submitted: "Tähän varaukseen on jo annettu palaute.",
          invalid_pin: "PIN-koodi on virheellinen.",
        };

        if (!reason) {
          if (status === 400) return { code: "bad_request", message: "Tarkista kentät ja yritä uudelleen." };
          if (status === 401) return { code: "unauthorized", message: "Ei oikeuksia suorittaa tätä toimintoa." };
          if (status === 403) return { code: "forbidden", message: "Toiminto ei ole sallittu." };
          if (status === 404) return { code: "not_found", message: map.not_found };
          if (status === 409) return { code: "conflict", message: "Toimintoa ei voi suorittaa (ristiriita)." };
          if (status === 410) return { code: "expired", message: map.expired };
          return { code: "error", message: err?.message || "Tuntematon virhe." };
        }

        return { code: reason, message: map[reason] || "Toiminto epäonnistui." };
      },
    }
  ),

  analyticsView: (payload = {}, opts) =>
    req("/analytics/view", { method: "POST", body: json(payload), ...(opts || {}) }),

  /* ------------ Media (uudet) ------------ */
  media: {

        /**
     * Profiilikuvan asetus TIEDOSTOSTA (FormData).
     * Palauttaa päivitetyn band-olion jos saatavilla, muuten { ok:true, profile_image_url }.
     */
async setProfileFile(bandId, file, { signal } = {}) {
  const id = encodeURIComponent(bandId);

  const fd = new FormData();
  fd.append("image", file);

  // TÄRKEÄ:
  // Profiilikuvan uploadissa ei saa käyttää mitään legacy-/fallback-reittejä.
  // Ainoa sallittu reitti on backendin korjattu:
  // POST /api/bands/:id/profile-image
  //
  // Vanhoista fallbackeista ainakin joku tekee 800x800-neliön.
  const res = await req(`/bands/${id}/profile-image`, {
    method: "POST",
    body: fd,
    signal,
  });

  const band = res?.band ?? res?.data?.band ?? res?.item ?? null;

  if (band) {
    return band;
  }

  const url =
    res?.profile_image_url ||
    res?.url ||
    res?.image_url ||
    res?.profile_image ||
    "";

  if (url) {
    return {
      ok: true,
      profile_image_url: url,
    };
  }

  return res;
},

    /**
     * Profiilikuvan asetus URL:ista (myös presetit kuten /delva-media/fallbacks/profile-1.webp).
     * Tallentaa URL:n tietokantaan. Palauttaa päivitetyn band-olion jos saatavilla.
     */
    async setProfileUrl(bandId, url, { signal } = {}) {
      const id = encodeURIComponent(bandId);
      const body = JSON.stringify({ url });

      const triesUrl = [
        { path: `/bands/${id}/profile-image/url`, method: "POST", body },
        { path: `/bands/${id}/profile-image`,     method: "PUT",  body: JSON.stringify({ profile_image_url: url }) },
        { path: `/bands/${id}/profile`,           method: "PUT",  body: JSON.stringify({ profile_image_url: url }) },
        { path: `/bands/${id}`,                   method: "PUT",  body: JSON.stringify({ profile_image_url: url }) },
        // Joissain skeemoissa kentän nimi vaihtelee:
        { path: `/bands/${id}`,                   method: "PUT",  body: JSON.stringify({ profile_image: url }) },
        { path: `/bands/${id}`,                   method: "PUT",  body: JSON.stringify({ profile_image_override: url }) },
      ];

      let lastErr;
      for (const t of triesUrl) {
        try {
          const res = await req(t.path, { method: t.method, body: t.body, signal });
          const band = res?.band ?? res?.data?.band ?? res?.item ?? null;
          if (band) return band;
          const outUrl =
            res?.profile_image_url ||
            res?.url ||
            res?.image_url ||
            res?.profile_image ||
            url;
          return { ok: true, profile_image_url: outUrl };
        } catch (e) {
          lastErr = e;
          if (e?.status === 404 || e?.status === 405) continue;
          throw e;
        }
      }
      throw lastErr || new Error("Profiilikuvan URL-asetus epäonnistui");
    },

    /** Sokeri: käytä valmista preset-kuvaa (ohjaa setProfileUrl:iin) */
    usePresetProfile(bandId, presetUrl, opts) {
      return this.setProfileUrl(bandId, presetUrl, opts);
    },

      /** Galleria: lisää kuva tiedostosta (FormData) */

/** Galleria: lisää kuva tiedostosta (FormData) – reitti- ja kenttäf fallbackit */
async uploadGalleryImage(bandId, file, { signal } = {}) {
  const id = encodeURIComponent(bandId);

  // Muodosta FormData halutulla kenttänimellä
  const makeFD = (field) => {
    const fd = new FormData();
    // jotkut endpointit odottavat images[] taulukkoa
    if (field.endsWith("[]")) fd.append(field, file, file?.name || "image");
    else fd.append(field, file, file?.name || "image");
    return fd;
  };

  // 1) Suorat galleriapolut – kokeile yleisimmät kenttänimet
  const fields = ["image", "file", "gallery_image", "photo", "images[]"];
  const paths = [
    `/bands/${id}/gallery`,
    `/bands/${id}/gallery-images`,
    `/bands/${id}/images`,
  ];

  let lastErr;
  for (const field of fields) {
    for (const p of paths) {
      try {
        const res = await req(p, { method: "POST", body: makeFD(field), signal });
        const raw = res?.image ?? res?.item ?? res;
        return norm.image(raw);
      } catch (e) {
        lastErr = e;
        const code = String(e?.payload?.error || "").toLowerCase();
        // Jos reitti puuttuu/metodi kielletty → kokeile seuraavaa
        if (e?.status === 404 || e?.status === 405) continue;
        // Jos uploader kaatuu/virheellinen kenttä → kokeile seuraavaa
        if (e?.status === 400 || e?.status === 413 || code === "upload_failed" || code === "invalid_field") continue;
        // Muut virheet: älä niele
        // (esim. 401/403 tms. on oikea virhe)
        throw e;
      }
    }
  }

  // 2) Fallback: yleinen uploaderi -> rekisteröi URL gallerialle
  try {
    const up = await req(`/uploads/image?scope=gallery&band_id=${id}`, {
      method: "POST",
      body: makeFD("image"),
      signal,
    });

    const url =
      up?.url || up?.image_url || up?.location || up?.path || up?.file || null;
    if (!url) throw new Error("no_url_from_uploader");

    // Kokeile useita body-muotoja ja polkuja URL:n rekisteröintiin
    const bodies = [{ url }, { image_url: url }, { path: url }, { images: [url] }];
    const urlPaths = [
      `/bands/${id}/gallery/url`,
      `/bands/${id}/gallery`,
    ];

    // Yritä sekä POST että PUT
    for (const method of ["POST", "PUT"]) {
      for (const body of bodies) {
        try {
          const res = await reqFirstOk(urlPaths, {
            method,
            body: JSON.stringify(body),
          });
          const raw = res?.image ?? res?.item ?? { url };
          return norm.image(raw);
        } catch (e) {
          if (e?.status === 404 || e?.status === 405) continue;
          // jos joku muu virhe, kokeillaan vielä seuraava body tai metodi
        }
      }
    }

    // Jos backissa ei ole URL-reittiä, palautetaan silti URL (UI voi päivittää oman listansa)
    return { ok: true, url };
  } catch (e) {
    const err = lastErr || e;
    err.payload = err.payload || { error: "upload_failed" };
    throw err;
  }
},


// services/api.js (ote)
// Tallentaa bändin setit PG-reitille (PUT /bands/:id/sets)
// src/services/api.js (ote)
// src/services/api.js (ote)

    async deleteGalleryImage(bandId, imageId, { signal } = {}) {
      await req(`/bands/${encodeURIComponent(bandId)}/gallery/${encodeURIComponent(imageId)}`, {
        method: "DELETE",
        signal,
      });
      return true;
    },

    async addVideo(bandId, url, { signal } = {}) {
      const data = await req(`/bands/${encodeURIComponent(bandId)}/videos`, {
        method: "POST",
        body: json({ url }),
        signal,
      });
      return norm.video(data?.video ?? data?.item ?? data);
    },

    async deleteVideo(bandId, videoId, { signal } = {}) {
      await req(`/bands/${encodeURIComponent(bandId)}/videos/${encodeURIComponent(videoId)}`, {
        method: "DELETE",
        signal,
      });
      return true;
    },

    async addAudio(bandId, url, { signal } = {}) {
      const data = await req(`/bands/${encodeURIComponent(bandId)}/audio`, {
        method: "POST",
        body: json({ url }),
        signal,
      });
      return norm.audio(data?.audio ?? data?.item ?? data);
    },

    async deleteAudio(bandId, audioId, { signal } = {}) {
      await req(`/bands/${encodeURIComponent(bandId)}/audio/${encodeURIComponent(audioId)}`, {
        method: "DELETE",
        signal,
      });
      return true;
    },
  },

    /* ------------ Ohjelmisto / biisilista ------------ */
  repertoire: {
    // Lisää uusi kappale esiintyjän ohjelmistoon.
    async add(bandId, payload = {}, { signal } = {}) {
      const data = await req(
        `/bands/${encodeURIComponent(bandId)}/repertoire`,
        {
          method: "POST",
          body: json({
            artist: payload.artist,
            title: payload.title,
            youtube_url: payload.youtube_url || null,
          }),
          signal,
        }
      );

      return data?.item ?? data;
    },

    // Muokkaa olemassa olevaa kappaletta.
    async update(bandId, itemId, payload = {}, { signal } = {}) {
      const data = await req(
        `/bands/${encodeURIComponent(bandId)}/repertoire/${encodeURIComponent(itemId)}`,
        {
          method: "PUT",
          body: json({
            artist: payload.artist,
            title: payload.title,
            youtube_url: payload.youtube_url || null,
          }),
          signal,
        }
      );

      return data?.item ?? data;
    },

    // Poista kappale ohjelmistosta.
    async remove(bandId, itemId, { signal } = {}) {
      await req(
        `/bands/${encodeURIComponent(bandId)}/repertoire/${encodeURIComponent(itemId)}`,
        {
          method: "DELETE",
          signal,
        }
      );

      return true;
    },
  },


  /* ------------ Setit (band sets) ------------ */
  sets: {
    async save(bandId, rows = [], opts = {}) {
      const id = encodeURIComponent(bandId);
      const path = `/bands/${id}/sets`;

      const toLabel = (s) => {
        const l = String(s.label || "").trim();
        if (l) return l;
        const c = +s.set_count, m = +s.minutes_per_set;
        return c > 0 && m > 0 ? `${c}x${m}min` : "";
      };

      const items = (rows || [])
        .map((s, i) => ({
          id: s.id ?? undefined,
          label: toLabel(s),
          set_count: Number(s.set_count) || 0,
          minutes_per_set: Number(s.minutes_per_set) || 0,
          position: Number.isFinite(+s.position) ? +s.position : i,
        }))
        .filter(s => s.set_count > 0 && s.minutes_per_set > 0);

      if (!items.length) return { ok: true, items: [] };

      return req(path, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
        body: JSON.stringify({ items }),
        ...opts,
      });
    },
  },

  /* ------------ Set definitions (global library) ------------ */
  setDefs: {
    async upsertMany(defs = [], opts = {}) {
      const items = (defs || []).map(d => ({
        set_count: Number(d.set_count) || 0,
        minutes_per_set: Number(d.minutes_per_set) || 0,
        label: `${Number(d.set_count) || 0}x${Number(d.minutes_per_set) || 0}min`,
      }));

      const tries = [
        { path: '/set_defs',       method: 'POST', body: { items } },
        { path: '/admin/set_defs', method: 'POST', body: { items } },
        { path: '/set-defs',       method: 'POST', body: { items } },
      ];

      let lastErr;
      for (const t of tries) {
        try {
          return await req(t.path, {
            method: t.method,
            body: json(t.body),   // käytä samaa json()-helperia kuin muuallakin
            ...(opts || {}),
          });
        } catch (e) {
          lastErr = e;
          if (e?.status === 404 || e?.status === 405) continue; // kokeile seuraavaa reittiä
          throw e;
        }
      }
      throw lastErr || new Error('set_defs upsert failed');
    },
  },

  /* ------------ Pricing (regions + save + quote) ------------ */
  pricing: {
    // Hae maakunnat objektimuodossa [{id,name,slug}]
    // 1) /bands/regions  → [{id,name,slug?}]
    // 2) fallback /meta/regions → [{id,name}] (slug rakennetaan nimestä)
    async regions(opts) {
      const slugify = (s = "") =>
        String(s)
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
          .replace(/[äÆæ]/gi, "a")
          .replace(/[öØø]/gi, "o")
          .replace(/[å]/gi, "a")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");

      // 1) Uusi PG-reitti
      try {
        const rows = await req(`/bands/regions`, opts);
        if (Array.isArray(rows)) {
          return rows
            .map(r => ({
              id: Number(r.id),
              name: String(r.name || "").trim(),
              slug: (r.slug ? String(r.slug) : slugify(r.name || "")).trim(),
            }))
            .filter(r => Number.isFinite(r.id) && r.name);
        }
      } catch (_) { /* jatka fallbackiin */ }

      // 2) Fallback: /meta/regions → [{id,name}]
      try {
        const rows = await req(`/meta/regions`, opts);
        if (Array.isArray(rows)) {
          return rows
            .map(r => ({
              id: Number(r.id),
              name: String(r.name || "").trim(),
              slug: slugify(r.name || ""),
            }))
            .filter(r => Number.isFinite(r.id) && r.name);
        }
      } catch (_) {}

      return [];
    },

    // Tallenna/set-replace hinnat (tukee replaceAll + region-scope). Backwards compatible.
    async save(bandId, entries, opts = {}) {
      const {
        replaceAll = false,
        region_id,
        region,
        customer_type,
        customerType,
        customer,
        ...fetchOpts
      } = opts || {};

      const normalizeCustomerType = (v) => {
        const t = String(v ?? "").trim().toLowerCase();
        if (t === "business") return "business";
        if (t === "private") return "private";
        return null;
      };

      const topCustomerType =
        normalizeCustomerType(customer_type ?? customerType ?? customer) || null;

      // Hyväksy alias-kentät: set_id/band_set_id, price/base_price, region_id/region
      const topRegionId   = Number(region_id);
      const topRegionName = region ? String(region) : "";

      const items = (entries || [])
        .map((p) => {
          // price alias
          const rawPrice = p.price ?? p.base_price;
          const num = rawPrice === null || rawPrice === undefined || rawPrice === ""
            ? null
            : Number(String(rawPrice).replace(",", "."));
          if (num === null || !Number.isFinite(num)) return null;

          const out = {};
          // set id alias
          const bsid = Number(p.band_set_id ?? p.set_id);
          const lbl  = String(p.set_label ?? p.duration ?? "").trim();
          const seasonKey = String(p.season_key ?? p.seasonKey ?? "").trim();

          // region alias: per-item → top-level id → nimet
          const rid   = Number(p.region_id);
          const rname = String(p.region ?? p.region_name ?? p.name ?? "").trim();

          if (Number.isFinite(bsid)) out.band_set_id = bsid;
          if (!out.band_set_id && lbl) out.set_label = lbl;

          if (Number.isFinite(rid)) out.region_id = rid;
          else if (Number.isFinite(topRegionId)) out.region_id = topRegionId;
          else if (rname) out.region = rname;
          else if (topRegionName) out.region = topRegionName;

          // customer_type: per-item → top-level → (ei pakollinen, mutta business vaatii)
          const itemCtype =
            normalizeCustomerType(p.customer_type ?? p.customerType ?? p.customer) ||
            topCustomerType ||
            null;
          if (itemCtype) out.customer_type = itemCtype;

           // season_key: välitetään backendille sellaisenaan jos annettu
          if (seasonKey) out.season_key = seasonKey;

          out.price = num;
          return out;
        })
        .filter(Boolean)
        // pidä vain rivit, joissa sekä setti (id tai label) että alue (id tai nimi)
        .filter((it) => (it.band_set_id || it.set_label) && (it.region_id || it.region));

      const body = {
        items,
        ...(replaceAll ? { replaceAll: true } : {}),
        ...(Number.isFinite(topRegionId) ? { region_id: topRegionId } : {}),
        ...(topRegionName ? { region: topRegionName } : {}),
        ...(topCustomerType ? { customer_type: topCustomerType } : {}),
      };

      if (
        typeof process !== "undefined" &&
        process.env &&
        process.env.NODE_ENV !== "production"
      ) {
        // eslint-disable-next-line no-console
        console.debug("pricing.save ->", body);
      }

      return req(`/bands/${encodeURIComponent(bandId)}/pricing`, {
        method: "POST",
        body: json(body),
        ...fetchOpts,
      });
    },

    // Upserttaa useita (band_set_id + region_id + price) kerralla
      async upsertMany(bandId, items, opts = {}) {
      const normalizeCustomerType = (v) => {
        const t = String(v ?? "").trim().toLowerCase();
        if (t === "business") return "business";
        if (t === "private") return "private";
        return null;
      };
      const topCustomerType =
        normalizeCustomerType(opts.customer_type ?? opts.customerType ?? opts.customer) || null;

      const payload = {
        items: (items || []).map(x => ({
          band_set_id: Number(x.band_set_id),
          region_id: Number(x.region_id),
          price: (x.price == null || x.price === "")
            ? null
            : Number(String(x.price).replace(",", ".")),
            ...(normalizeCustomerType(x.customer_type ?? x.customerType ?? x.customer) || topCustomerType
            ? { customer_type: normalizeCustomerType(x.customer_type ?? x.customerType ?? x.customer) || topCustomerType }
            : {}),
        }))
      };
      return req(`/bands/${encodeURIComponent(bandId)}/pricing`, {
        method: "POST",
        body: json(payload),
        ...(opts || {})
      });
    },

    // Poista yhden setin hinta tietyltä alueelta (ensisijaisesti DELETE-reitti)
    async deletePrice(bandId, bandSetId, regionId, opts) {
      try {
        await req(
          `/bands/${encodeURIComponent(bandId)}/pricing/${encodeURIComponent(bandSetId)}/${encodeURIComponent(regionId)}`,
          { method: "DELETE", ...(opts || {}) }
        );
        return { ok: true };
      } catch (e) {
        // Fallback vanhoille ympäristöille: price:null POSTilla
        if (e?.status === 404 || e?.status === 405 || e?.status === 400) {
          await req(`/bands/${encodeURIComponent(bandId)}/pricing`, {
            method: "POST",
            body: json({
              items: [{ band_set_id: Number(bandSetId), region_id: Number(regionId), price: null }]
            }),
            ...(opts || {})
          });
          return { ok: true, fallback: true };
        }
        throw e;
      }
    },

    // ---- UUTTA: poista + palauta tuore lista (näkyy UI:ssa heti) ----
    async deletePriceAndList(bandId, bandSetId, regionId, opts) {
      await api.pricing.deletePrice(bandId, bandSetId, regionId, opts);
      const pricing = await api.pricing.list(bandId, opts);
      return { ok: true, pricing };
    },

    // Poista kaikki hinnat yhdeltä maakunnalta (DELETE → fallback POST replaceAll)
    async clearRegion(bandId, regionId, opts) {
      try {
        await req(
          `/bands/${encodeURIComponent(bandId)}/pricing/region/${encodeURIComponent(regionId)}`,
          { method: "DELETE", ...(opts || {}) }
        );
        return { ok: true };
      } catch (e) {
        if (e?.status === 404 || e?.status === 405 || e?.status === 400) {
          await req(`/bands/${encodeURIComponent(bandId)}/pricing`, {
            method: "POST",
            body: json({ replaceAll: true, region_id: Number(regionId), items: [] }),
            ...(opts || {})
          });
          return { ok: true, fallback: true };
        }
        throw e;
      }
    },

    // Poista setti-labelin hinnat kaikista maakunnista
    async deleteSetEverywhere(bandId, setLabel, opts) {
      const lbl = String(setLabel || "").trim();
      if (!lbl) return { ok: true }; // ei mitään poistettavaa
      try {
        await req(
          `/bands/${encodeURIComponent(bandId)}/pricing/set`,
          { method: "DELETE", body: json({ set_label: lbl }), ...(opts || {}) }
        );
        return { ok: true };
      } catch (e) {
        // Fallback: serveri ilman reittiä → ei kaadeta
        if (e?.status === 404 || e?.status === 405) return { ok: true, fallback: true };
        throw e;
      }
    },

      async clearRegionAndList(bandId, regionId, opts) {
      await api.pricing.clearRegion(bandId, regionId, opts);
      const pricing = await api.pricing.list(bandId, opts);
      return { ok: true, pricing };
    },

    // Poista labelin hinta yksittäiseltä alueelta (jos ei band_set_id:tä käytettävissä)
    async deleteByLabelInRegion(bandId, setLabel, regionId, opts) {
      const lbl = String(setLabel || "").trim();
      const rid = Number(regionId);
      if (!lbl || !Number.isFinite(rid)) return { ok: true };
      try {
        await req(
          `/bands/${encodeURIComponent(bandId)}/pricing/label`,
          { method: "DELETE", body: json({ set_label: lbl, region_id: rid }), ...(opts || {}) }
        );
        return { ok: true };
      } catch (e) {
         if (e?.status === 404 || e?.status === 405 || e?.status === 400) {
          // Fallback: POST price:null
          await req(`/bands/${encodeURIComponent(bandId)}/pricing`, {
            method: "POST",
            body: json({ items: [{ set_label: lbl, region_id: rid, price: null }] }),
            ...(opts || {})
          });
          return { ok: true, fallback: true };
        }
        throw e;
      }
    },

    async deleteByLabelInRegionAndList(bandId, setLabel, regionId, opts) {
      await api.pricing.deleteByLabelInRegion(bandId, setLabel, regionId, opts);
      const pricing = await api.pricing.list(bandId, opts);
      return { ok: true, pricing };
    },

    // Hae tallennetut rivit: noudetaan bändin profiilista (band.pricing)
    async list(bandId, opts) {
      const b = await api.bandByIdOrSlug(bandId, opts);
      return Array.isArray(b?.pricing) ? b.pricing : [];
    },

    // Valmiit setti-labelit bändin seteistä
    async setLabels(bandId, { signal } = {}) {
      const b = await api.bandByIdOrSlug(bandId, { signal });
      const labels = Array.isArray(b?.sets)
        ? Array.from(new Set(b.sets.map(s => s?.label).filter(Boolean)))
        : [];
      return labels;
    },

    // Hinta-arvio (uusi: GET /bookings/price, fallback legacyyn /bands/bookings/price)
    async quote({ slug, region, duration, distance = 0, customer_type, customerType, customer }, opts) {
      const normCtype = (v) => {
        const t = String(v ?? "").trim().toLowerCase();
        if (t === "business") return "business";
        if (t === "private") return "private";
        return null;
      };
      const ct = normCtype(customer_type ?? customerType ?? customer);

      const q = new URLSearchParams({
        slug: String(slug ?? ""),
        region: String(region ?? ""),
        duration: String(duration ?? ""),
        distance: String(distance ?? 0),
        ...(ct ? { customer_type: ct } : {}),
      });

      return reqFirstOk(
        [`/bookings/price?${q.toString()}`, `/bands/bookings/price?${q.toString()}`],
        { method: "GET", ...(opts || {}) }
      );
    },
  },
};

// Altista hook myös api-olion kautta (AuthContext odottaa tätä)
api.onUnauthorized = onUnauthorized;
// (valinnainen) reset stub, jos AuthContext kutsuu api.reset()
if (typeof api.reset !== "function") api.reset = () => {};

let __bandsDictionariesCache = null;
let __bandsDictionariesPromise = null;

let __bandsEventTypesCache = null;
let __bandsEventTypesPromise = null;

// --- Bands: dictionaries (instruments & services) ---
api.bands.dictionaries = async (opts = {}) => {
  // Dictionary-data muuttuu harvoin, joten pidetään se selaimen muistissa
  // saman session aikana. Tämä estää saman raskaan kutsun toistumisen.
  if (__bandsDictionariesCache) {
    return __bandsDictionariesCache;
  }

  // Jos sama kutsu on jo käynnissä, odota sitä äläkä aloita uutta.
  if (__bandsDictionariesPromise) {
    return __bandsDictionariesPromise;
  }

  __bandsDictionariesPromise = req("/bands/dictionaries", opts)
    .then((data) => {
      __bandsDictionariesCache = data;
      return data;
    })
    .finally(() => {
      __bandsDictionariesPromise = null;
    });

  return __bandsDictionariesPromise;
};

// --- Bands: event types (kevyt etusivulle) ---
api.bands.eventTypes = async (opts = {}) => {
  // Tilaisuustyypit muuttuvat harvoin, joten pidetään ne selaimen muistissa
  // saman session aikana. Tämä välttää raskaan /bands/dictionaries-haun etusivulla.
  if (__bandsEventTypesCache) {
    return __bandsEventTypesCache;
  }

  // Jos sama haku on jo käynnissä, odota sitä.
  if (__bandsEventTypesPromise) {
    return withAbortSignal(__bandsEventTypesPromise, opts?.signal);
  }

  // Jaettu haku EI käytä yksittäisen komponentin AbortControlleria,
  // jotta React StrictMode -abortti ei käynnistä samaa hakua uudelleen.
  __bandsEventTypesPromise = req("/bands/dictionaries", {
    ...(opts || {}),
    signal: undefined,
  })
    .then((data) => {
      __bandsEventTypesCache = data;
      return data;
    })
    .finally(() => {
      __bandsEventTypesPromise = null;
    });

  return withAbortSignal(__bandsEventTypesPromise, opts?.signal);
};

// Palauttaa suoraan backendin muodon:
// { instruments: [{ id, label, group? }, ...], services: [{ id, label, group? }, ...] }

api.bands.availability = (params = {}, opts) => {
  const { name, slug } = params || {};
  if (!name && !slug) throw new Error("name tai slug on annettava");
  const q = new URLSearchParams();
  if (name) q.set("name", name);
  if (slug) q.set("slug", slug);
  return req(`/bands/availability?${q.toString()}`, opts);
};

// --- Admin/bookings: keikka- ja inquiry-detallit ---
api.gigs = {
  async get(id, { signal } = {}) {
    const gid = encodeURIComponent(id);
    const data = await reqFirstOk(
      [`/admin/gigs/${gid}`, `/gigs/${gid}`, `/bookings/gigs/${gid}`],
      { signal }
    );
    return data?.gig || data?.data || data;
  },
};

// --- Bookings: owner day details (vain omistajille) ---
api.bookings = {
  /** GET /api/bookings/owner/day?band_id=:id&date=YYYY-MM-DD */
  ownerDay(bandId, date, { signal, debug } = {}) {
    const q = new URLSearchParams({
      band_id: String(bandId),
      date,
      v: String(Date.now()), // välimuistin välttämiseksi
    });
    if (debug) q.set("debug", "1");
    return req(`/bookings/owner/day?${q.toString()}`, { signal });
  },
    /** DEBUG: vertaile samaa päivää owner/day, calendar-sec ja healthz/busy */
  async __probeDay(bandId, date, { signal } = {}) {
    const own = api.bookings.ownerDay(bandId, date, { signal, debug: true })
      .catch(e => ({ error: e?.status || e?.message || String(e) }));
    const cal = req(`/bookings/calendar-sec/${encodeURIComponent(bandId)}?from=${encodeURIComponent(date)}&to=${encodeURIComponent(date)}`, { signal })
      .catch(e => ({ error: e?.status || e?.message || String(e) }));
    const busy = req(`/bookings/healthz/busy?band_id=${encodeURIComponent(bandId)}&date=${encodeURIComponent(date)}`, { signal })
      .catch(e => ({ error: e?.status || e?.message || String(e) }));
    const [owner, calendar, healthz] = await Promise.all([own, cal, busy]);
    return { owner, calendar, healthz };
  },
};

// --- Memberships: poista nykyinen käyttäjä bändistä ("Poista kortti tililtä")
// Käy läpi yleisimmät reitit (DELETE), ja varareittinä POST /leave.
api.memberships = {
  async leave(bandId, opts) {
    const id = encodeURIComponent(bandId);
    let lastErr;
    for (const path of [
      `/bands/${id}/membership`,
      `/bands/${id}/memberships/me`,
      `/users/me/bands/${id}`,
      `/admin/bands/${id}/memberships/me`,
    ]) {
      try {
        return await req(path, { method: "DELETE", ...(opts || {}) });
      } catch (e) {
        lastErr = e;
        if (e?.status === 404 || e?.status === 405) continue; // kokeile seuraavaa
        throw e;
      }
    }
    // Varareitti: POST-tyylinen *leave*
    try {
      return await req(`/bands/${id}/memberships/leave`, {
        method: "POST",
        ...(opts || {}),
      });
    } catch (e) {
      throw lastErr || e;
    }
  },
};

// --- Bands: alias-julkaisu sekä kokonaan poistaminen (varovasti)
// Dashboard kutsuu api.bands.unpublish -> ohjataan publishBand(false)
api.bands.publish   = (id) => api.publishBand(id, true);
api.bands.unpublish = (id) => api.publishBand(id, false);

// Salli Dashboardin fallback: api.bands.removeMembership -> sama kuin memberships.leave
api.bands.removeMembership = (bandId, opts) => api.memberships.leave(bandId, opts);

 api.bands.delete = (bandId, opts) =>
   req(`/bands/${encodeURIComponent(bandId)}`, {
     method: "DELETE", ...(opts || {})
   });

 // Tarvittaessa erillinen admin-versio
 api.bands.deleteAsAdmin = (bandId, opts) =>
   req(`/admin/bands/${encodeURIComponent(bandId)}`, {
     method: "DELETE", ...(opts || {})
   });

  // ---------- Util: resolveBandId('user' | slug | id) -> number | null ----------
async function __resolveBandId_internal(param, { signal } = {}) {
  const v = String(param || '').trim();

  // 1) Ei paramia tai "user" -> hae /auth/me ja valitse owner tai eka
  if (!v || v === 'user') {
    const me = await req('/auth/me', { signal });
    const bands = me?.bands || me?.user?.bands || [];
    const mine = bands.find(b => b.is_owner) || bands[0];
    const id = mine?.id ?? mine?.band_id;
    return Number.isFinite(+id) ? +id : null;
  }

  // 2) Pelkkä numero -> suoraan id
  if (/^\d+$/.test(v)) return +v;

  // 3) Muuten slug -> hae ja nosta id
  const slug = encodeURIComponent(v);
  const hasToken = !!getAuthToken();
  const data = await reqFirstOk(
    [
      `/bands/${slug}`,
      `/bands?slug=${slug}`,
      ...(hasToken ? [`/admin/bands/by-slug/${slug}`] : []),
    ],
    { signal }
  );

    // nosta id myös listamuodoista: {items:[...]} tai suora [...]
  const first = Array.isArray(data?.items) ? data.items[0]
                : (Array.isArray(data) ? data[0] : null);
  const id = data?.id ?? data?.band?.id ?? data?.item?.id ?? first?.id;

  
  return Number.isFinite(+id) ? +id : null;
}

// Julkiseksi api.utiliin
api.util = {
  ...(api.util || {}),
  resolveBandId: (param, opts) => __resolveBandId_internal(param, opts),
};

// Devtoolsissa helppo tarkistus:
if (typeof window !== "undefined") {
  window.__API_BASE__ = API_BASE;
  window.__AUTH_BASE__ = AUTH_BASE;
  window.__API_DEBUG__ = __API_DEBUG__;
    // debug-käyttö: altista api konsoliin
  try {
    window.api = window.api || api;
    window.__bbx_api__ = api;
    window.__onUnauthorized__ = onUnauthorized;
  } catch {}
}

// ---------------------------------------------------------
// Julkinen AI-haku / suosittelija
// POST /api/public/ai-search
// ---------------------------------------------------------
api.aiSearch = (query, { limit = 10, date = null, signal } = {}) => {
  return req("/public/ai-search", {
    method: "POST",
    body: JSON.stringify({
      query,
      limit,
      ...(date ? { date } : {}),
    }),
    signal,
  });
};

// DevTools: nopea tarkistus että JWT on muistissa
api.__debugToken = () => {
  const t = getAuthToken();
  console.info("[JWT present?]", !!t, t ? (t.slice(0,10) + "…") : null);
  return !!t;
};

export const getBand     = (idOrSlug, opts) => api.bandByIdOrSlug(idOrSlug, opts);
export const getRegions  = (opts)          => api.pricing.regions(opts);
export const putBandSets = (bandId, items) => api.sets.save(bandId, items);
export const postPricing = (bandId, region_id, items) =>
  api.pricing.save(bandId, items, { replaceAll: true, region_id });